// src/api/endpoints.ts - ИСПРАВЛЕННАЯ ВЕРСИЯ
import apiClient from './client'
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  Event,
  CreateEventRequest,
  UpdateEventRequest,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  Partner,  // ДОБАВИТЬ ЭТУ СТРОКУ
  CreatePartnerRequest,  // ДОБАВИТЬ ЭТУ СТРОКУ
  UpdatePartnerRequest,  // ДОБАВИТЬ ЭТУ СТРОКУ
  Notification,
  PaginatedResponse,
  SearchParams,
  AppSettings,
  FileUpload,
  ContactMessage,
  ContactMessageUpdate,
  ContactMessageStats
} from './types'

// Auth endpoints
export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return await apiClient.post<LoginResponse>('/auth/login', credentials)
  },

  async register(userData: RegisterRequest): Promise<User> {
    return await apiClient.post<User>('/auth/register', userData)
  },

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout')
    apiClient.clearAuthToken()
  },

  async getCurrentUser(): Promise<User> {
    return await apiClient.get<User>('/auth/me')
  },

  async refreshToken(): Promise<LoginResponse> {
    return await apiClient.post<LoginResponse>('/auth/refresh')
  },

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email })
  },

  async resetPassword(token: string, password: string): Promise<void> {
    await apiClient.post('/auth/reset-password', { token, password })
  }
}

// Users endpoints - ИСПРАВЛЕНО
export const usersApi = {
  async getUsers(params?: SearchParams): Promise<PaginatedResponse<User>> {
    return await apiClient.get<PaginatedResponse<User>>('/users', { params })
  },

  async getUserById(id: number): Promise<User> {
    return await apiClient.get<User>(`/users/${id}`)
  },

  // ИСПРАВЛЕНО: правильная типизация
  async updateUser(id: number, userData: Partial<User>): Promise<User> {
    return await apiClient.put<User>(`/users/${id}`, userData)
  },

  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/users/${id}`)
  },

  async updateProfile(userData: Partial<User>): Promise<User> {
    return await apiClient.put<User>('/users/profile', userData)
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/users/change-password', {
      old_password: oldPassword,
      new_password: newPassword
    })
  },

  async uploadAvatar(file: File, onProgress?: (progress: number) => void): Promise<FileUpload> {
    return await apiClient.uploadFile<FileUpload>('/users/avatar', file, onProgress)
  }
}

// Posts endpoints
export const postsApi = {
  async getPosts(params?: SearchParams): Promise<PaginatedResponse<Post>> {
    return await apiClient.get<PaginatedResponse<Post>>('/posts', { params })
  },

  async getPostById(id: number): Promise<Post> {
    return await apiClient.get<Post>(`/posts/${id}`)
  },

  async createPost(postData: CreatePostRequest): Promise<Post> {
    return await apiClient.post<Post>('/posts', postData)
  },

  async updatePost(id: number, postData: UpdatePostRequest): Promise<Post> {
    return await apiClient.put<Post>(`/posts/${id}`, postData)
  },

  async deletePost(id: number): Promise<void> {
    await apiClient.delete(`/posts/${id}`)
  },

  async publishPost(id: number): Promise<Post> {
    return await apiClient.patch<Post>(`/posts/${id}/publish`)
  },

  async unpublishPost(id: number): Promise<Post> {
    return await apiClient.patch<Post>(`/posts/${id}/unpublish`)
  },

  async getPostsByUser(userId: number, params?: SearchParams): Promise<PaginatedResponse<Post>> {
    return await apiClient.get<PaginatedResponse<Post>>(`/posts/user/${userId}`, { params })
  },

  async searchPosts(query: string, params?: SearchParams): Promise<PaginatedResponse<Post>> {
    return await apiClient.get<PaginatedResponse<Post>>('/posts/search', {
      params: { q: query, ...params }
    })
  }
}

// Events endpoints
export const eventsApi = {
  async getEvents(params?: SearchParams): Promise<PaginatedResponse<Event>> {
    return await apiClient.get<PaginatedResponse<Event>>('/events', { params })
  },

  async getEventById(id: number): Promise<Event> {
    return await apiClient.get<Event>(`/events/${id}`)
  },

  async createEvent(eventData: CreateEventRequest): Promise<Event> {
    return await apiClient.post<Event>('/events', eventData)
  },

  async updateEvent(id: number, eventData: UpdateEventRequest): Promise<Event> {
    return await apiClient.put<Event>(`/events/${id}`, eventData)
  },

  async deleteEvent(id: number): Promise<void> {
    await apiClient.delete(`/events/${id}`)
  },

  async publishEvent(id: number): Promise<Event> {
    return await apiClient.patch<Event>(`/events/${id}/publish`)
  },

  async unpublishEvent(id: number): Promise<Event> {
    return await apiClient.patch<Event>(`/events/${id}/unpublish`)
  },

  async getEventsByUser(userId: number, params?: SearchParams): Promise<PaginatedResponse<Event>> {
    return await apiClient.get<PaginatedResponse<Event>>(`/events/user/${userId}`, { params })
  },

  async searchEvents(query: string, params?: SearchParams): Promise<PaginatedResponse<Event>> {
    return await apiClient.get<PaginatedResponse<Event>>('/events/search', {
      params: { q: query, ...params }
    })
  }
}

// Organizations endpoints
export const organizationsApi = {
  async getOrganizations(params?: SearchParams): Promise<PaginatedResponse<Organization>> {
    return await apiClient.get<PaginatedResponse<Organization>>('/organizations', { params })
  },

  async getOrganizationById(id: number): Promise<Organization> {
    return await apiClient.get<Organization>(`/organizations/${id}`)
  },

  async createOrganization(orgData: CreateOrganizationRequest): Promise<Organization> {
    return await apiClient.post<Organization>('/organizations', orgData)
  },

  async updateOrganization(id: number, orgData: UpdateOrganizationRequest): Promise<Organization> {
    return await apiClient.put<Organization>(`/organizations/${id}`, orgData)
  },

  async deleteOrganization(id: number): Promise<void> {
    await apiClient.delete(`/organizations/${id}`)
  }
}

// Files endpoints
export const filesApi = {
  async uploadFile(file: File, onProgress?: (progress: number) => void): Promise<FileUpload> {
    return await apiClient.uploadFile<FileUpload>('/files/upload', file, onProgress)
  },

  async getFileInfo(fileId: string): Promise<FileUpload> {
    return await apiClient.get<FileUpload>(`/files/${fileId}`)
  },

  async deleteFile(fileId: string): Promise<void> {
    await apiClient.delete(`/files/${fileId}`)
  },

  async downloadFile(fileId: string): Promise<Blob> {
    return await apiClient.get(`/files/${fileId}/download`, {
      responseType: 'blob'
    })
  }
}

// App settings endpoints
export const settingsApi = {
  async getSettings(): Promise<AppSettings> {
    return await apiClient.get<AppSettings>('/settings')
  },

  async updateSettings(settings: Partial<AppSettings>): Promise<AppSettings> {
    return await apiClient.put<AppSettings>('/settings', settings)
  }
}

// Health check endpoints
export const healthApi = {
  async checkHealth(): Promise<{ status: string; timestamp: string }> {
    return await apiClient.get('/health')
  },

  async checkDatabase(): Promise<{ status: string; response_time: number }> {
    return await apiClient.get('/health/database')
  },

  async checkRedis(): Promise<{ status: string; response_time: number }> {
    return await apiClient.get('/health/redis')
  }
}

// Notifications endpoints
export const notificationsApi = {
  async getNotifications(params?: SearchParams): Promise<PaginatedResponse<Notification>> {
    return await apiClient.get('/notifications', { params })
  },

  async markAsRead(notificationId: number): Promise<void> {
    await apiClient.patch(`/notifications/${notificationId}/read`)
  },

  async markAllAsRead(): Promise<void> {
    await apiClient.patch('/notifications/read-all')
  },

  async deleteNotification(notificationId: number): Promise<void> {
    await apiClient.delete(`/notifications/${notificationId}`)
  }
}

// WebSocket endpoints (для установки соединения)
export const websocketApi = {
  getWebSocketUrl(token?: string): string {
    const baseUrl = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000'
    return token ? `${baseUrl}/ws?token=${token}` : `${baseUrl}/ws`
  },

  getChatWebSocketUrl(roomId: string, token?: string): string {
    const baseUrl = import.meta.env.VITE_WS_BASE_URL || 'ws://localhost:8000'
    return token
      ? `${baseUrl}/ws/chat/${roomId}?token=${token}`
      : `${baseUrl}/ws/chat/${roomId}`
  }
}

// Batch operations
export const batchApi = {
  async batchDelete(endpoint: string, ids: number[]): Promise<void> {
    await apiClient.post(`${endpoint}/batch-delete`, { ids })
  },

  async batchUpdate(endpoint: string, updates: Array<{ id: number; data: any }>): Promise<void> {
    await apiClient.post(`${endpoint}/batch-update`, { updates })
  }
}

// Partners endpoints
export const partnersApi = {
  async getPartners(params?: SearchParams): Promise<PaginatedResponse<Partner>> {
    return await apiClient.get<PaginatedResponse<Partner>>('/partners', { params })
  },

  async getPartnerById(id: number): Promise<Partner> {
    return await apiClient.get<Partner>(`/partners/${id}`)
  },

  async createPartner(partnerData: CreatePartnerRequest): Promise<Partner> {
    return await apiClient.post<Partner>('/partners', partnerData)
  },

  async updatePartner(id: number, partnerData: UpdatePartnerRequest): Promise<Partner> {
    return await apiClient.put<Partner>(`/partners/${id}`, partnerData)
  },

  async deletePartner(id: number): Promise<void> {
    await apiClient.delete(`/partners/${id}`)
  }
}
// Contact Messages endpoints
export const contactMessagesApi = {
  async getContactMessages(params?: SearchParams): Promise<PaginatedResponse<ContactMessage>> {
    return await apiClient.get<PaginatedResponse<ContactMessage>>('/contact-messages', { params })
  },

  async getContactMessageById(id: number): Promise<ContactMessage> {
    return await apiClient.get<ContactMessage>(`/contact-messages/${id}`)
  },

  async markAsRead(id: number): Promise<ContactMessage> {
    return await apiClient.patch<ContactMessage>(`/contact-messages/${id}/mark-read`)
  },

  async updateContactMessage(id: number, data: ContactMessageUpdate): Promise<ContactMessage> {
    return await apiClient.patch<ContactMessage>(`/contact-messages/${id}`, data)
  },

  async deleteContactMessage(id: number): Promise<void> {
    await apiClient.delete(`/contact-messages/${id}`)
  },

  async getStats(): Promise<ContactMessageStats> {
    return await apiClient.get<ContactMessageStats>('/contact-messages/stats/summary')
  }
}
// Export всех API групп
export const api = {
  auth: authApi,
  users: usersApi,
  posts: postsApi,
  events: eventsApi,
  organizations: organizationsApi,
  partners: partnersApi,
  contactMessages: contactMessagesApi,
  files: filesApi,
  settings: settingsApi,
  health: healthApi,
  notifications: notificationsApi,
  websocket: websocketApi,
  batch: batchApi
}
